import React from 'react';
import { Link } from 'react-router-dom';
import { HeroAnimation } from '../components/HeroAnimation';
import { BrandIcon } from '../components/BrandIcon';
import { AiSupport } from '../components/AiSupportIcon';
import { ArrowRight, Briefcase, Wrench, Headset, Sparkles } from 'lucide-react';

const Home = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with 3D Element */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 to-white">
        {/* Background pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              repeating-linear-gradient(0deg, transparent, transparent 1px, #00D4B4 1px, #00D4B4 calc(1px + 1px)),
              repeating-linear-gradient(90deg, transparent, transparent 1px, #00D4B4 1px, #00D4B4 calc(1px + 1px))
            `,
            backgroundSize: '80px 80px'
          }}
        />

        <div className="container mx-auto px-[7.6923%] relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="inline-block">
                <span className="text-[#00D4B4] text-lg font-medium tracking-wide uppercase">Customer Experience Excellence</span>
              </div>
              
              <h1 className="text-[36px] sm:text-[32px] md:text-[48px] lg:text-[66px] font-semibold leading-[1.1] tracking-[-0.62px] text-gray-900">
                Transform Customer Experience Like Never Before
              </h1>
              
              <p className="text-[20px] font-medium leading-[1.5] text-gray-600">
                With 15+ years of proven expertise, I build world-class CX departments for SaaS, eCommerce, and auction technology companies. Turn customer satisfaction into your competitive advantage.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 sm:gap-8 pt-8">
                <div>
                  <div className="text-[32px] md:text-[48px] font-semibold text-[#00D4B4] leading-none">15+</div>
                  <div className="text-gray-600 mt-2">Years Experience</div>
                </div>
                <div>
                  <div className="text-[32px] md:text-[48px] font-semibold text-[#00D4B4] leading-none">95%+</div>
                  <div className="text-gray-600 mt-2">Client Satisfaction</div>
                </div>
                <div>
                  <div className="text-[32px] md:text-[48px] font-semibold text-[#00D4B4] leading-none">5x</div>
                  <div className="text-gray-600 mt-2">Award Winner</div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4 sm:gap-6 pt-8">
                <Link 
                  to="/contact"
                  className="bg-[#00D4B4] text-white px-6 py-4 text-lg font-medium hover:bg-[#00BFA3] transition-all duration-400 flex items-center gap-3 min-h-[56px] shadow-md hover:shadow-lg"
                  style={{ borderRadius: '0px' }}
                >
                  Get Started
                  <ArrowRight className="w-5 h-5" />
                </Link>
                
                <Link 
                  to="/impact"
                  className="bg-white text-gray-900 border-2 border-gray-300 px-6 py-4 text-lg font-medium hover:border-[#00D4B4] hover:text-[#00D4B4] transition-all duration-400 flex items-center gap-3 min-h-[56px]"
                  style={{ borderRadius: '0px' }}
                >
                  View Impact
                </Link>
              </div>
            </div>

            {/* Right — Animated CX Illustration */}
            <div className="relative h-[340px] sm:h-[440px] lg:h-[700px] flex items-center justify-center">
              <HeroAnimation />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32 relative bg-white">
        <div className="container mx-auto px-[7.6923%]">
          <div className="text-center mb-20">
            <h2 className="text-[32px] md:text-[48px] font-semibold leading-[1.1] tracking-[-0.02em] text-gray-900 mb-6">
              Specialized CX Solutions
            </h2>
            <p className="text-[20px] text-gray-600 max-w-3xl mx-auto">
              Three core consulting pillars — plus AI — to turn support into a growth engine
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Briefcase,
                title: 'Fractional COO & Operations Advisory',
                description: 'Retainer-based strategic operations leadership, workflow overhauls, and executive guidance.'
              },
              {
                icon: Wrench,
                title: 'Support Architecture & Tech Stack Optimization',
                description: 'A 4-week sprint to transform bloated, siloed ticketing systems and messy workflows into a streamlined, automated support engine.'
              },
              {
                icon: Headset,
                title: 'Voice of the Customer & De-Escalation Frameworks',
                description: 'Custom feedback loops and frontline training programs focused on high-stakes communication and retention.'
              },
              {
                icon: AiSupport,
                title: 'AI-Powered Support',
                description: 'Deploy AI-driven support infrastructure that resolves routine tickets automatically, freeing your team for the conversations that need a human. My own AI implementation cut ticket volume by 25% without hurting satisfaction scores.',
                amber: true
              }
            ].map((service, index) => (
              <div 
                key={index}
                className="bg-gray-50 p-8 border border-gray-200 hover:border-[#00D4B4] hover:shadow-lg transition-all duration-400 group flex flex-col"
              >
                <BrandIcon icon={service.icon} variant={service.amber ? 'amber' : 'teal'} className="mb-6 group-hover:scale-110 transition-transform duration-400" />
                <h3 className="text-[24px] font-semibold text-gray-900 mb-4">
                  {service.title}
                </h3>
                {service.amber && (
                  <span
                    data-testid="home-cert-badge"
                    className="inline-flex items-center gap-2 self-start bg-[#E1F5EE] text-[#085041] text-[12px] font-semibold px-3 py-1.5 mb-4"
                  >
                    <Sparkles className="w-3.5 h-3.5" /> Google AI Certified
                  </span>
                )}
                <p className="text-[18px] text-gray-600 leading-[1.5] flex-1">
                  {service.description}
                </p>
                <Link to="/contact" data-testid={`solution-cta-${index}`} className="inline-flex items-center gap-2 text-[16px] font-medium text-[#00D4B4] hover:gap-3 transition-all duration-300 mt-6">
                  Book a consultation <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose CX Sage */}
      <section className="py-20 md:py-32 bg-gray-50 relative">
        <div className="container mx-auto px-[7.6923%]">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <h2 className="text-[32px] md:text-[48px] font-semibold leading-[1.1] tracking-[-0.02em] text-gray-900 mb-8">
                15+ Years of CX Excellence
              </h2>
              <p className="text-[20px] text-gray-600 leading-[1.5] mb-8">
                I've spent my career building customer experience departments from the ground up — scaling support teams, cutting resolution times, and turning support from a cost center into a revenue driver. CX Sage brings that same playbook to SaaS, eCommerce, and auction technology companies.              </p>
              <div className="space-y-6">
                {[
                  'Lean Six Sigma certified',
                  'SaaS-specific CX strategies',
                  'AI-driven support infrastructure',
                  'Google AI Professional Certificate',
                  'ITIL 4 Foundation & Managing Professional',
                  'Auction and marketplace CX experience'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-2 h-2 bg-[#00D4B4] mt-3 flex-shrink-0" />
                    <p className="text-[18px] text-gray-600">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="bg-white p-8 border border-gray-200 shadow-md">
                <div className="text-[#00D4B4] text-[24px] md:text-[32px] font-semibold mb-2">5x</div>
                <div className="text-gray-900 text-[20px] font-medium mb-2">Stevie Award Winner</div>
                <div className="text-gray-600 text-[16px]">Recognition for customer service excellence and innovation</div>
              </div>

              <div className="bg-white p-8 border border-gray-200 shadow-md">
                <div className="text-[#00D4B4] text-[24px] md:text-[32px] font-semibold mb-2">Lean</div>
                <div className="text-gray-900 text-[20px] font-medium mb-2">Six Sigma Certified</div>
                <div className="text-gray-600 text-[16px]">Process improvement methodology for operational excellence</div>
              </div>

              <div className="bg-white p-8 border border-gray-200 shadow-md">
                <div className="text-[#00D4B4] text-[24px] md:text-[32px] font-semibold mb-2">AI</div>
                <div className="text-gray-900 text-[20px] font-medium mb-2">Google AI Professional Certificate</div>
                <div className="text-gray-600 text-[16px]">Applied AI training powering smarter, automated support</div>
              </div>

              <div className="bg-white p-8 border border-gray-200 shadow-md">
                <div className="text-[#00D4B4] text-[24px] md:text-[32px] font-semibold mb-2">ITIL 4</div>
                <div className="text-gray-900 text-[20px] font-medium mb-2">Foundation &amp; Managing Professional</div>
                <div className="text-gray-600 text-[16px]">Global best-practice framework for IT and support service management</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 relative bg-gradient-to-br from-[#00D4B4] to-[#00BFA3]">
        <div className="container mx-auto px-[7.6923%]">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-[32px] md:text-[48px] font-semibold leading-[1.1] tracking-[-0.02em] text-white mb-8">
              Ready to Transform Your Customer Experience?
            </h2>
            <p className="text-[20px] text-white/95 leading-[1.5] mb-12">
              Let's discuss how we can build a world-class CX department tailored to your business needs.
            </p>
            <Link 
              to="/contact"
              className="inline-flex bg-white text-[#00D4B4] px-8 py-5 text-lg font-medium hover:bg-gray-100 transition-all duration-400 items-center gap-3 min-h-[56px] shadow-lg"
              style={{ borderRadius: '0px' }}
            >
              Schedule a Consultation
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
